import { List, Map, Set } from 'immutable';
import { constants as multiviewGridConstants } from '@saucelabs/sl-component-multiview-grid';
import semver from 'semver';

import * as globalTypes from '../../types';
import types from './types';
import { DeviceDescriptor } from './records';
import constants from './constants';

const {
  LS_MOBILE_SELECTION_CURRENT_VIEW,
  MOBILE_SELECTION_FILTER_CATEGORY: FILTER_CATEGORY,
  MOBILE_SELECTION_FACETS,
  MOBILE_SELECTION_FILTER,
} = constants;

const { MANUFACTURER, RESOLUTION, OS_VERSION, SCREEN_SIZE } = MOBILE_SELECTION_FACETS;
const { ANDROID, IOS } = MOBILE_SELECTION_FILTER;

export const sortResolutions = (resA, resB) => {
  const widthSortValue = Number(resA.split('x')[0]) - Number(resB.split('x')[0]);

  return widthSortValue !== 0 ?
    widthSortValue :
    // if width is the same, sort by height
    Number(resA.split('x')[1]) - Number(resB.split('x')[1]);
};

const sortNumericStrings = (el1, el2) => el1.localeCompare(el2, undefined, { numeric: true });

export const prepareFacets = (facets) => Object.entries(facets).reduce((acc, [facet, value]) => {
  if (facet === RESOLUTION) {
    return { ...acc, [facet]: Object.keys(value).sort(sortResolutions) };
  }

  if (facet === OS_VERSION || facet === SCREEN_SIZE) {
    return { ...acc, [facet]: Object.keys(value).sort(sortNumericStrings).reverse() };
  }

  return { ...acc, [facet]: Object.keys(value).sort() };
}, {});

export const filterFacetsByOs = (os, preparedFacets = {}, deviceDescriptors = []) => {
  if (!os) {
    return preparedFacets;
  }

  const filteredDevices = deviceDescriptors.filter((device) => device.os === os);

  return {
    ...preparedFacets,
    [MANUFACTURER]: preparedFacets[MANUFACTURER]?.filter((manufacturer) => (
      filteredDevices.some((device) => device.manufacturers.includes(manufacturer))
    )),
    [OS_VERSION]: preparedFacets[OS_VERSION]?.filter((osVersion) => (
      filteredDevices.some((device) => (
        semver.eq(semver.coerce(device.osVersion), semver.coerce(osVersion))
      ))
    )),
    [RESOLUTION]: preparedFacets[RESOLUTION]?.filter((resolution) => (
      filteredDevices.some((device) => (
        device.getPrettyResolution().replace(/\s/g, '') === resolution
      ))
    )),
  };
};

const initialState = Map({
  activeFilters: Map({
    [FILTER_CATEGORY.CLOUD_TYPE]: Set([]),
    [FILTER_CATEGORY.COMPATIBILITY]: Set([]),
    [FILTER_CATEGORY.FORM_FACTOR]: Set([]),
    [FILTER_CATEGORY.OS]: Set([]),
    [FILTER_CATEGORY.OS_VERSION]: Set([]),
    [FILTER_CATEGORY.MANUFACTURER]: Set([]),
    [FILTER_CATEGORY.NETWORK_TYPE]: Set([]),
    [FILTER_CATEGORY.RESOLUTION]: Set([]),
  }),
  // Deprecated - Remove once web-ui-new-rdc-device-status is resolved
  availableDeviceIds: [],
  // Deprecated - Remove once web-ui-new-rdc-device-status is resolved
  availableDeviceIdsLoaded: false,
  currentFacets: null,
  // TODO: Move localStorage access to an operation (during re-ducks refactoring)
  currentView:
    window.localStorage.getItem(LS_MOBILE_SELECTION_CURRENT_VIEW) ||
    multiviewGridConstants.VIEW_TYPE.SMALL_BOX,
  devicesStatus: [],
  initialFacets: null,
  isDevicesStatusLoaded: false,
  items: List(),
  loading: false,
  pinnedDeviceCompositeIdsLoaded: false,
  starDevicesCompositeIds: Set(),
  searchTerm: '',
  sessionAvailable: false,
  sessionAvailabilityLoaded: false,
  sortMode: constants.DEVICE_SORT_MODES.PREFERRED_FIRST,
});

/**
 * Undoes the adding/removal of a star if there's an action FAILURE or CANCEL
 * @param {Immutable.Set} starDevicesCompositeIds - current list of starred real devices
 * @param {{ added: DeviceDescriptor, removed: DeviceDescriptor }}
 *   added/removed device upon the REQUEST of this action; only one of these should be present
 * @returns {Immutable.Set} real devices with/without the device that was unsucessfully
 *   added/removed
 */
const undoStar = (starDevicesCompositeIds, { added, removed }) => (
  added ?
    starDevicesCompositeIds.delete(added.compositeId) :
    starDevicesCompositeIds.add(removed.compositeId)
);

const realDeviceSelectionReducer = (state = initialState, { type, payload, meta }) => {
  switch (type) {
    case globalTypes.METADATA_GET.REQUEST:
      return state.set('pinnedDeviceCompositeIdsLoaded', false);
    case globalTypes.METADATA_GET.SUCCESS: {
      const metadata = payload.items.reduce((result, { key, value }) => (
        { [key]: value, ...result }
      ), {});

      return state.withMutations((mutableState) => (
        mutableState
          .set('pinnedDeviceCompositeIdsLoaded', true)
          .set('starDevicesCompositeIds', Set(metadata.real_devices_starred_by_composite_id))
      ));
    }

    case types.UPDATE_STARRED_REAL_DEVICES.REQUEST: {
      return state.set('starDevicesCompositeIds', payload);
    }
    case types.UPDATE_STARRED_REAL_DEVICES.FAILURE:
    case types.UPDATE_STARRED_REAL_DEVICES.CANCEL: {
      return state.set('starDevicesCompositeIds', undoStar(state.get('starDevicesCompositeIds'), meta));
    }
    case types.SET_MOBILE_SELECTION_CURRENT_VIEW:
      // TODO: Move localStorage access to an operation (during re-ducks refactoring)
      window.localStorage.setItem(LS_MOBILE_SELECTION_CURRENT_VIEW, payload);
      return state.set('currentView', payload);

    case types.SET_FILTERS: {
      const filtersToUpdate = Array.isArray(payload) ? payload : [payload];

      return state.withMutations((mutableState) => {
        filtersToUpdate.forEach((newFilter) => {
          const { category, values } = newFilter;
          mutableState.setIn(['activeFilters', category], Set(values));
        });
      });
    }

    case types.CLEAR_FILTERS:
      return state.set('activeFilters', initialState.get('activeFilters'));

    case types.UPDATE_MOBILE_SELECTION_SEARCH_TERM:
      return state.set('searchTerm', payload.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'));

    case types.MOBILE_DEVICES_FETCH.REQUEST:
      return state.set('loading', true);
    case types.MOBILE_DEVICES_FETCH.SUCCESS: {
      const payloadItems = payload.entities || [];
      const items = List(payloadItems.map((device) => new DeviceDescriptor(device)));
      const preparedFacets = payload.facets && prepareFacets(payload.facets);

      return state.withMutations((mutable) => (
        mutable
          .set('loading', false)
          .set('items', items)
          .set('currentFacets', {
            [ANDROID]: filterFacetsByOs(ANDROID, preparedFacets, items),
            [IOS]: filterFacetsByOs(IOS, preparedFacets, items),
            ALL: preparedFacets,
          } || null)
          .update('initialFacets', (initialFacets) => initialFacets || preparedFacets || null)));
    }
    case types.MOBILE_DEVICES_FETCH.FAILURE:
      return state.set('loading', false);
    // Deprecated - Remove once web-ui-new-rdc-device-status is resolved
    case types.MOBILE_AVAILABLE_DEVICE_IDS_FETCH.SUCCESS:
      return state.withMutations((mutable) => (
        mutable
          .set('availableDeviceIds', payload)
          .set('availableDeviceIdsLoaded', true)
      ));
    // Deprecated - Remove once web-ui-new-rdc-device-status is resolved
    case types.MOBILE_AVAILABLE_DEVICE_IDS_FETCH.FAILURE:
      return state.withMutations((mutable) => (
        mutable
          .set('availableDeviceIds', initialState.get('availableDeviceIds'))
          .set('availableDeviceIdsLoaded', false)
      ));
    case types.MOBILE_DEVICES_STATUS_FETCH.SUCCESS:
      return state.withMutations((mutable) => (
        mutable
          .set('devicesStatus', payload.devices)
          .set('isDevicesStatusLoaded', true)
      ));
    case types.MOBILE_DEVICES_STATUS_FETCH.FAILURE:
      return state.withMutations((mutable) => (
        mutable
          .set('devicesStatus', initialState.get('devicesStatus'))
          .set('isDevicesStatusLoaded', false)
      ));
    case types.MOBILE_SESSION_AVAILABLE_FETCH.SUCCESS:
      return state.withMutations((mutable) => (
        mutable
          .set('sessionAvailable', payload.manualSessionAvailable)
          .set('sessionAvailabilityLoaded', true)
      ));
    case types.MOBILE_SESSION_AVAILABLE_FETCH.FAILURE:
      return state.withMutations((mutable) => (
        mutable
          .set('sessionAvailable', initialState.get('sessionAvailable'))
          .set('sessionAvailabilityLoaded', false)
      ));
    case types.SORT_DEVICES:
      return state.set('sortMode',
        Object.values(constants.DEVICE_SORT_MODES).includes(payload) ? payload : state.get('sortMode'),
      );
    default:
      return state;
  }
};

export default realDeviceSelectionReducer;
export { initialState };
